import { readActiveHomeRole } from "./homeRole";
import { userCanEditPlanningSession } from "./planningAccess";
import { kotMatchesForCurrentRound } from "./kotCoachRound";

/**
 * Whether the training room should show coach/staff controls.
 * Respects Home dashboard role picker: dual-role users viewing as Player get player UI.
 * Session conductors (coach, monitor, admin, moderator, …) all share Live Training
 * controls — there is no exclusive host; multiple connected staff may drive the session.
 */
export function isCoachViewForTraining(user, permissions) {
  if (!user) return false;
  const activeRole = readActiveHomeRole(user);
  if (activeRole === "player") return false;
  return userCanEditPlanningSession(user, permissions);
}

/** Alias for clarity at call sites. */
export const isCoachForTraining = isCoachViewForTraining;

/**
 * Resolve roster player_id for the logged-in user.
 * @param {object|null|undefined} user
 * @param {Array<object>} rosterRows
 * @returns {string|null}
 */
export function resolveViewerPlayerId(user, rosterRows) {
  if (!user || !Array.isArray(rosterRows)) return null;
  const uid = user.user_id != null ? String(user.user_id) : null;
  if (!uid) return null;

  const byUser = rosterRows.find(
    (p) => p?.user_id != null && String(p.user_id) === uid
  );
  if (byUser?.player_id != null) return String(byUser.player_id);

  if (user.player_id != null) return String(user.player_id);
  return null;
}

/**
 * @param {string|null} viewerPlayerId
 * @param {Array<object>} rosterRows
 */
export function isViewerPresentInSession(viewerPlayerId, rosterRows) {
  if (!viewerPlayerId || !Array.isArray(rosterRows)) return false;
  const pid = String(viewerPlayerId);
  return rosterRows.some(
    (p) =>
      p?.player_id != null &&
      String(p.player_id) === pid &&
      p.presensee === true
  );
}

/** Session roster row for the logged-in player (from grpplayerlist). */
export function isPlayerPresentInTrainingSession(playerRecord) {
  return playerRecord?.presensee === true;
}

export function matchInvolvesPlayer(match, playerId) {
  if (!match || !playerId) return false;
  const pid = String(playerId);
  return (
    (match.player_a_id != null && String(match.player_a_id) === pid) ||
    (match.player_b_id != null && String(match.player_b_id) === pid)
  );
}

/**
 * @param {object|null} snapshot
 * @param {string|null} playerId
 */
export function findMyKotMatch(snapshot, playerId) {
  if (!snapshot || !playerId) return null;
  const matches = kotMatchesForCurrentRound(snapshot);
  return matches.find((m) => matchInvolvesPlayer(m, playerId)) || null;
}

/**
 * Match currently assigned to a physical table (table-phone / table camera).
 * @param {object|null} snapshot
 * @param {number|string|null} tableNumber
 */
export function findKotMatchAtTable(snapshot, tableNumber) {
  if (!snapshot) return null;
  const n = Number(tableNumber);
  if (!Number.isFinite(n) || n <= 0) return null;
  const matches = kotMatchesForCurrentRound(snapshot);
  return (
    matches.find((m) => Number(m?.table_number) === n) || null
  );
}
